<template>
    <v-dialog
        :value="isOpen"
        scrollable
        max-width="700px"
        transition="slide-y-transition"
        origin="top center"
        eager
        @keydown.esc="onClose"
        @click:outside="onClose"
    >
        <v-card>
            <v-card-title>
                <template v-if="isNewWare">
                    <h1 class="text-subtitle-1">
                        Dodaj towar
                    </h1>
                </template>
                <template v-else>
                    <h1 class="text-subtitle-1">
                        Edycja towaru
                    </h1>
                </template>
            </v-card-title>

            <div class="mx-5 my-1">
                <v-row>
                    <v-col cols="12">
                        <v-select
                            v-model="wareModel.wareName"
                            :items="products"
                            label="Produkt"
                            item-text="name"
                            return-object
                            outlined
                            dense
                            :hint="isNewWare && isProductAmountFullAssigned ? 'Produkt w pełni rozpisany.' : ''"
                            persistent-hint
                            :disabled="!isNewWare"
                            :error-messages="productErrors"
                            @change="changeProduct"
                        />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" sm="6">
                        <v-autocomplete
                            v-model="packageModel"
                            label="Opakowanie"
                            :items="packageTypesData"
                            item-text="name"
                            return-object
                            outlined
                            dense
                            :error-messages="packagePackageTypeErrors"
                            @change="changePackageType"
                        />
                    </v-col>
                    <v-col cols="12" sm="6">
                        <v-text-field
                            v-model="wareModel.wareAmount"
                            v-mask="'#######'"
                            label="Nakład"
                            outlined
                            dense
                            persistent-hint
                            :hint="wareAmountInfo"
                            :disabled="isNewWare && isProductAmountFullAssigned"
                            :error-messages="wareAmountErrors"
                            @change="changeAmount"
                        />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" sm="4">
                        <v-text-field
                            v-model="packageModel.length"
                            label="Długość"
                            suffix="mm"
                            outlined
                            dense
                            :error-messages="packageLengthErrors"
                            :disabled="!isCustomMadeCardBoard"
                        />
                    </v-col>
                    <v-col cols="12" sm="4">
                        <v-text-field
                            v-model="packageModel.width"
                            label="Szerokość"
                            suffix="mm"
                            outlined
                            dense
                            :error-messages="packageWidthErrors"
                            :disabled="!isCustomMadeCardBoard"
                        />
                    </v-col>
                    <v-col cols="12" sm="4">
                        <v-text-field
                            v-model="packageModel.height"
                            label="Wysokość"
                            suffix="mm"
                            outlined
                            dense
                            :error-messages="packageHeightErrors"
                            :disabled="!isCustomMadeCardBoard"
                        />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" sm="6">
                        <v-text-field
                            v-model="packageModel.weight"
                            label="Waga opakowania"
                            type="number"
                            step="0.005"
                            suffix="kg"
                            :min="0"
                            outlined
                            dense
                            :error-messages="packageWeightErrors"
                            :disabled="!isCustomMadeCardBoard"
                        />
                    </v-col>
                    <v-col cols="12" sm="6">
                        <v-text-field
                            v-model="packageModel.maxWeightCapacity"
                            label="Nośność opakowania"
                            type="number"
                            step="0.1"
                            suffix="kg"
                            :min="0"
                            outlined
                            dense
                            :error-messages="packageMaxWeightCapacityErrors"
                            :disabled="!isCustomMadeCardBoard"
                            @change="changeAmount"
                        />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" sm="auto">
                        <v-checkbox
                            v-model="packageModel.setManuallyAmount"
                            class="mt-2"
                            label="Ręczna edycja ilości opakowań"
                            hide-details
                            outlined
                            dense
                            :disabled="withoutSpecifiedCardbox"
                            @change="changeAmount"
                        />
                    </v-col>
                    <v-col cols="12" sm="3">
                        <v-text-field
                            v-model="wareModel.wareUnitPackageQuantity"
                            label="Ilość opakowań"
                            type="number"
                            suffix="szt."
                            :min="0"
                            outlined
                            dense
                            :disabled="!setAmountManually"
                            :error-messages="wareUnitPackageQuantityErrors"
                        />
                    </v-col>
                </v-row>
            </div>
            <v-divider />

            <v-card-actions>
                <v-btn
                    color="error"
                    text
                    data-sentry-label="Zamknij"
                    @click="onClose"
                >
                    Anuluj
                </v-btn>
                <v-spacer />
                <v-btn
                    color="success"
                    text
                    :data-sentry-label="isNewWare ? 'Dodaj' : 'Zapisz'"
                    :disabled="(products.length === 0) || (isNewWare && isProductAmountFullAssigned)"
                    @click="onSave"
                >
                    <template v-if="isNewWare">
                        Dodaj
                    </template>
                    <template v-else>
                        Zapisz
                    </template>
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { required, integer, between, minValue } from 'vuelidate/lib/validators'
import { isEmpty } from 'lodash'
import { mask } from 'vue-the-mask'

export default {
    name: 'ProductDialog',
    directives: { mask },
    props: {
        isOpen: {
            type: Boolean
        },
        productsData: {
            type: Array,
            default: () => []
        },
        wareData: {
            type: Object,
            default: null
        },
        shippingOrderData: {
            type: Object,
            default: null
        },
        packageData: {
            type: Object,
            default: null
        },
        packageTypesData: {
            type: Array,
            default: null
        },
    },
    data() {
        return {
            wareModel: {
                wareName: null,
                wareAmount: null,
                wareUnitPackageQuantity: 0,
                wareUnitPackageTypeId: null,
                wareUnitPackageWeight: 0,
                singleItemWareWeight: 0
            },
            packageModel: {
                packageType: null,
                packageTypeId: null,
                amount: 0,
                length: null,
                width: null,
                height: null,
                setManuallyAmount: false,
                maxWeightCapacity: 0.0,
                packageWeight: 0.0
            },
            shippingOrder: {},
            products: [],
            shippingOrderPositionPackageWaresAll: [],
        }
    },
    validations() {
        const rules = {
            wareModel: {
                wareName: {
                    required,
                    isUnique: function() {
                        if (!isEmpty(this.packageData)) {
                            return !(this.isNewWare && this.packageData.shippingOrderPositionPackageWares.some(item => item.wareName === this.wareModel.wareName))
                        }

                        return true
                    },
                },
                wareAmount: {
                    required,
                    integer,
                    between: between(1, this.wareAmountToAssign)
                },
                wareUnitPackageQuantity: {
                    required,
                    minValue: minValue(0),
                    integer 
                }
            },
            packageModel: {
                id: {
                    required
                },
                maxWeightCapacity: {
                    required,
                    minValue: minValue(0.1)
                },
                length: {
                    required,
                    minValue: this.isCustomMadeCardBoard ? minValue(1) : minValue(0)
                },
                width: {
                    required,
                    minValue: this.isCustomMadeCardBoard ? minValue(1) : minValue(0)
                },
                height: {
                    required,
                    minValue: this.isCustomMadeCardBoard ? minValue(1) : minValue(0)
                },
                weight: {
                    required,
                    minValue: this.isCustomMadeCardBoard ? minValue(0.005) : minValue(0)
                }
            }
        }

        return rules
    },
    computed: {
        isCustomMadeCardBoard() {
            return this.wareModel.wareUnitPackageTypeId === 46
        },
        withoutSpecifiedCardbox() {
            return this.wareModel.wareUnitPackageTypeId === 49
        },
        isNewWare() {
            return isEmpty(this.wareData)
        },
        wareAmountInfo() {
            const wareAmount = this.wareModel.wareAmount ? this.wareModel.wareAmount : 0

            return this.wareModel.wareName ? `${wareAmount} / ${this.wareAmountToAssign}` : ''
        },
        wareAmountToAssign() {
            const product = this.products.find(item => item.name === this.wareModel.wareName)

            if (!product) return 0

            let assignedProductAmount = 0

            this.shippingOrderPositionPackageWaresAll.forEach(item => {
                if (item.wareName === product.name) {
                    assignedProductAmount += parseInt(item.wareAmount)
                }
            })

            const result = product.amount - assignedProductAmount

            if (this.isNewWare) {
                return result >= 0 ? result : 0
            } else {
                return result >= 0 ? result + parseInt(this.wareData.wareAmount) : product.amount
            }
        },
        isProductAmountFullAssigned() {
            return this.wareModel.wareName !== null && this.wareAmountToAssign === 0
        },
        setAmountManually() {
            return this.packageModel.setManuallyAmount
        },
        // Walidacje
        productErrors() {
            const errors = []

            if (!this.$v.wareModel.wareName.$dirty) return errors
            !this.$v.wareModel.wareName.required && errors.push('Proszę wybrać produkt.')
            !this.$v.wareModel.wareName.isUnique && errors.push('Wybrany produkt istnieje już w danym pakowaniu.')

            return errors
        },
        wareAmountErrors() {
            const errors = []

            if (!this.$v.wareModel.wareAmount.$dirty) return errors

            if (!this.$v.wareModel.wareAmount.required) {
                errors.push('Proszę podać ilość')
            }

            if (!this.$v.wareModel.wareAmount.integer) {
                errors.push('Niepoprawny format.')
            }

            if (!this.$v.wareModel.wareAmount.required || !this.$v.wareModel.wareAmount.between && !(this.isNewWare && this.isProductAmountFullAssigned)) {
                errors.push(`Proszę podać wartość z zakresu 1-${this.wareAmountToAssign}`)
            }

            return errors
        },
        packagePackageTypeErrors() {
            const errors = []

            if (!this.$v.packageModel.id.$dirty) return errors
            !this.$v.packageModel.id.required && errors.push('Proszę podać rodzaj opakowania.')

            return errors
        },
        packageLengthErrors() {
            const errors = []

            if (!this.isCustomMadeCardBoard) return errors

            if (!this.$v.packageModel.length.$dirty) return errors

            if (!this.$v.packageModel.length.required || !this.$v.packageModel.length.minValue) {
                errors.push('Proszę podać długość.')
            }

            return errors
        },
        packageWidthErrors() {
            const errors = []

            if (!this.isCustomMadeCardBoard) return errors

            if (!this.$v.packageModel.width.$dirty) return errors

            if (!this.$v.packageModel.width.required || !this.$v.packageModel.width.minValue) {
                errors.push('Proszę podać szerokość.')
            }

            return errors
        },
        packageHeightErrors() {
            const errors = []

            if (!this.isCustomMadeCardBoard) return errors

            if (!this.$v.packageModel.height.$dirty) return errors

            if (!this.$v.packageModel.height.required || !this.$v.packageModel.height.minValue) {
                errors.push('Proszę podać wysokość.')
            }

            return errors
        },
        packageWeightErrors() {
            const errors = []

            if (!this.isCustomMadeCardBoard) return errors

            if (!this.$v.packageModel.weight.$dirty) return errors

            if (!this.$v.packageModel.weight.required || !this.$v.packageModel.weight.minValue) {
                errors.push('Proszę podać wagę.')
            }

            return errors
        },
        packageMaxWeightCapacityErrors() {
            const errors = []

            if (!this.withoutSpecifiedCardbox) return errors

            if (!this.$v.packageModel.maxWeightCapacity.$dirty) return errors

            if (!this.$v.packageModel.maxWeightCapacity.required || !this.$v.packageModel.maxWeightCapacity.minValue) {
                errors.push('Proszę podać maksymalną nośność.')
            }

            return errors
        },
        wareUnitPackageQuantityErrors() {
            const errors = []

            if (!this.setAmountManually) return errors

            if (!this.$v.wareModel.wareUnitPackageQuantity.required) {
                errors.push('Liczba opakowań jest wymagana')
            }

            if (!this.$v.wareModel.wareUnitPackageQuantity.minValue || !this.$v.wareModel.wareUnitPackageQuantity.integer) {
                errors.push('Proszę podać całkowitą dodatnią liczbę opakowań')
            }

            return errors
        },
    },
    watch: {
        isOpen(boolean) {
            if (boolean) {

                this.shippingOrder = JSON.parse(JSON.stringify(this.shippingOrderData))
                this.products = this.productsData ? JSON.parse(JSON.stringify(this.productsData)) : []
                this.package = JSON.parse(JSON.stringify(this.packageData))

                this.shippingOrderPositionPackageWaresAll = this.shippingOrder.shippingOrderPositions.flatMap(item => item.shippingOrderPositionPackages)
                    .flatMap(item => item.shippingOrderPositionPackageWares)

                this.products.forEach(item => {
                    item.packedAmount = 0

                    if (this.shippingOrderPositionPackageWaresAll.filter(ware => ware.wareName === item.name).length > 0) {
                        item.packedAmount = this.shippingOrderPositionPackageWaresAll.filter(ware => ware.wareName === item.name)
                            .map(ware => parseInt(ware.wareAmount))
                            .reduce((a, b) => parseInt(a) + parseInt(b))
                    }
                })

                if (!this.isNewWare) {
                    this.wareModel = JSON.parse(JSON.stringify(this.wareData))
                    if (this.wareData.wareUnitPackageTypeId) {
                        const packageType = this.packageTypesData.find(item => item.id === this.wareData.wareUnitPackageTypeId)

                        if (packageType) {
                            this.packageModel = packageType
                            
                            this.packageModel.height = this.wareModel.wareUnitPackageHeight ?? this.packageModel.height
                            this.packageModel.length = this.wareModel.wareUnitPackageLength ?? this.packageModel.length
                            this.packageModel.width = this.wareModel.wareUnitPackageWidth ?? this.packageModel.width
                            this.packageModel.weight = this.wareModel.wareUnitPackageWeight ?? this.packageModel.weight
                            this.packageModel.maxWeightCapacity = this.wareModel.maxWeightCapacity ?? this.packageModel.maxWeightCapacity
                        }
                    }
                } else {
                    if (this.products.length === 1) {
                        this.changeProduct(this.products[0])
                    }
                }
            } else {
                setTimeout(() => {
                    this.$v.$reset()
                    this.shippingOrder = {}
                    this.wareModel = {
                        wareName: null,
                        wareAmount: null,
                        wareUnitPackageQuantity: 0,
                        wareUnitPackageTypeId: null,
                        wareUnitPackageWeight: 0,
                        singleItemWareWeight: 0
                    },
                    this.packageModel = {},
                    this.products = []
                    this.package = {}
                    this.shippingOrderPositionPackageWaresAll = []
                }, 500)
            }
        }
    },
    methods: {
        changeProduct(product) {
            this.wareModel.wareName = product.name
            this.wareModel.wareAmount = this.wareAmountToAssign
            this.wareModel.singleItemWareWeight = product.weightSingle
            this.$v.wareModel.wareName.$touch()
        },
        changeAmount() {
            if (!this.packageModel.setManuallyAmount) {
                if (this.withoutSpecifiedCardbox) {
                    this.wareModel.wareUnitPackageQuantity = 0
                } else {
                    this.wareModel.wareUnitPackageQuantity = Math.ceil(this.wareModel.wareAmount * this.wareModel.singleItemWareWeight / this.packageModel.maxWeightCapacity)
                }
            }
        },
        changePackageType(packageType) {
            this.wareModel.wareUnitPackageTypeId = packageType.id
            if (packageType.id === 49) { // pakowanie luzem
                this.wareModel.wareUnitPackageQuantity = 0
                this.wareModel.wareUnitPackageWeight = 0
            } else {
                this.wareModel.wareUnitPackageQuantity = Math.ceil(this.wareModel.wareAmount * this.wareModel.singleItemWareWeight / packageType.maxWeightCapacity)
            }
        },
        onSave() {
            this.$v.$touch()

            if (this.$v.$invalid) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('validations.invalidMessage')
                })

                return
            }

            this.wareModel.wareUnitPackageHeight = this.packageModel.height
            this.wareModel.wareUnitPackageLength = this.packageModel.length
            this.wareModel.wareUnitPackageWidth = this.packageModel.width
            this.wareModel.wareUnitPackageWeight = this.packageModel.weight
            this.wareModel.maxWeightCapacity = this.packageModel.maxWeightCapacity

            const payload = JSON.parse(JSON.stringify(this.wareModel))

            this.$emit('on-save', payload)
        },
        onClose() {
            this.$emit('on-close')
        }
    }
}
</script>